import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/utils/api";
import { fmtDateTime } from "@/utils/format";
import { EXCEL } from "@/constants/testIds";
import { toast } from "sonner";
import { History, ChevronDown } from "lucide-react";
import { ModuleSelector }  from "@/components/excel/ModuleSelector";
import { FileUploader }    from "@/components/excel/FileUploader";
import { FieldMapper }     from "@/components/excel/FieldMapper";
import { ImportResult }    from "@/components/excel/ImportResult";
import { ImportProgress }  from "@/components/excel/ImportProgress";

const MODULES = [
  { value: "cariler",        label: "Cari Kartlar" },
  { value: "stoklar",        label: "Stok Tanımlar" },
  { value: "cari_hareketler", label: "Cari Hareket Raporu" },
];

const STEPS = [
  { n: 1, label: "Modül & Dosya" },
  { n: 2, label: "Alan Eşleme" },
  { n: 3, label: "Aktarılıyor" },
  { n: 4, label: "Tamamlandı" },
];

const POLL_INTERVAL_MS = 2000;

export default function ExcelImport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [integrity, setIntegrity] = useState(null);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);
  const checkIntegrity = async () => {
    setCheckingIntegrity(true);
    try { setIntegrity((await api.get("/excel-import/data-integrity")).data); }
    catch (error) { toast.error(formatApiError(error)); }
    finally { setCheckingIntegrity(false); }
  };
  const [step,         setStep]         = useState(1);
  const [modul,        setModul]        = useState("");
  const [file,         setFile]         = useState(null);
  const [preview,      setPreview]      = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [loading,      setLoading]      = useState(false);
  // Background-job state
  const [jobId,        setJobId]        = useState(null);
  const [jobStatus,    setJobStatus]    = useState(null);
  const [result,       setResult]       = useState(null);
  // Upload-log drawer
  const [logs,         setLogs]         = useState([]);
  const [showLogs,     setShowLogs]     = useState(false);
  const [selectedLog,  setSelectedLog]  = useState(null);

  const openLogDetail = async (log, type) => {
    try {
      const { data } = await api.get(`/excel-import/logs/${log.id}`);
      if (type === "inserted" && data.modul === "stoklar" && data.inserted_ids?.length) {
        navigate(`/stoklar?imported=${data.inserted_ids.join(",")}`);
        return;
      }
      if (type === "errors") setSelectedLog(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadErrors = () => {
    if (!selectedLog?.errors?.length) return;
    const rows = ["Hata", ...selectedLog.errors].map((value) => `"${String(value).replace(/"/g, '""')}"`);
    const blob = new Blob([`\uFEFF${rows.join("\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedLog.dosya_adi || "aktarim"}-hatalar.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Fetch upload history ────────────────────────────────────────────────────
  const fetchLogs = useCallback(() => {
    if (user?.role !== "admin") return;
    api.get("/excel-import/logs")
      .then((r) => setLogs(r.data))
      .catch(() => {}); // log fetch failures are non-critical
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLogs(); }, [fetchLogs, result]);

  // ── Poll job status (2 s interval) ─────────────────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    const isTerminal = jobStatus?.status === "done" || jobStatus?.status === "error";
    if (isTerminal) return;

    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/excel-import/status/${jobId}`);
        setJobStatus(data);

        if (data.status === "done") {
          setResult({
            message:      data.message,
            inserted:     data.inserted,
            updated:      data.updated,
            errors:       data.errors,
            total_errors: data.total_errors,
            inserted_ids: data.inserted_ids || [],
            modul:        data.modul,
          });
          setStep(4);
          fetchLogs();
          toast.success(data.message);
          clearInterval(timer);
        } else if (data.status === "error") {
          toast.error(data.message ?? "İçe aktarma hatası oluştu");
          clearInterval(timer);
        }
      } catch {
        // transient poll errors are ignored; interval retries automatically
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [jobId, jobStatus?.status, fetchLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFilePreview = async (e) => {
    e.preventDefault();
    if (!file || !modul) { toast.error("Modül ve dosya seçilmeli"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("modul", modul);
      const { data } = await api.post("/excel-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Use server-computed auto-mapping (handles aliases: ACIKLAMA1→oem_no1, etc.)
      const serverMap = data.suggested_mapping || {};
      const autoMap = {};
      (data.suggested_fields ?? []).forEach((f) => {
        if (serverMap[f]) {
          autoMap[f] = serverMap[f];
        } else {
          const match = data.columns.find(
            (c) => c.toLowerCase().replace(/\s/g, "_") === f.toLowerCase() || c.toLowerCase() === f.toLowerCase()
          );
          autoMap[f] = match ?? "";
        }
      });
      setFieldMapping(autoMap);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("modul", modul);
      fd.append("field_mapping", JSON.stringify(fieldMapping));
      const { data } = await api.post("/excel-import/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(data.job_id);
      setJobStatus({ status: "pending", progress: 0, message: "Başlatılıyor...", total: 0, processed: 0, inserted: 0, updated: 0 });
      setStep(3);
      toast.success("İçe aktarma arka planda başlatıldı");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setFieldMapping({});
    setResult(null);
    setJobId(null);
    setJobStatus(null);
    setModul("");
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Excel İçe Aktarma
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Verilerinizi Excel veya CSV formatında yükleyin</p>
          {modul === "cari_hareketler" && <p className="mt-2 max-w-2xl text-sm text-amber-700">Cari kodu veya kimliği, tarih, kaynak sistem ve dış kayıt numarası zorunludur. Aynı kaynaktan aynı cari ve dış kayıt numarası tekrar yüklenirse mevcut hareket güncellenir. Cari bakiye bu yüklemeyle değiştirilmez.</p>}
        </div>
        {user?.role === "admin" && <button
          onClick={() => setShowLogs((v) => !v)}
          className="flex items-center gap-2 text-sm text-slate-600 border border-slate-300 rounded-md px-3 py-2 hover:bg-slate-50 transition-colors"
        >
          <History size={15} /> Yükleme Geçmişi
          <ChevronDown size={13} className={showLogs ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>}
      </div>

      {/* Step indicator (4 steps) */}
      {user?.role === "admin" && <section className="rounded-lg border border-slate-200 bg-white p-4">
        <button onClick={checkIntegrity} disabled={checkingIntegrity} className="text-sm font-semibold text-[#0D1B3E] disabled:opacity-50">{checkingIntegrity ? "Kontrol ediliyor..." : "Veri Kontrolü"}</button>
        {integrity && <div className="mt-3 space-y-2 text-sm">
          <p>{integrity.message}</p>
          {[ ["stoklar", "Stok"], ["cariler", "Cari"] ].map(([key, label]) => <div key={key}>
            <b>{label} kodları: </b>{integrity[key]?.tekrarlanan_kodlar?.length ? <ul className="mt-1 space-y-1 break-all">{integrity[key].tekrarlanan_kodlar.map(row => <li key={row.kod}>{row.kod || "(boş kod)"}: {row.adet} kayıt</li>)}</ul> : "Tekrarlanan kod bulunmadı."}
            {integrity[key]?.devami_var && <p>İlk 50 kod gösteriliyor; daha fazla tekrar var.</p>}
          </div>)}
          <p>Dış kayıt numarası olmayan eski cari hareket: {integrity.kimliksiz_cari_hareket}. Bu kayıtlar kendiliğinden birleştirilmez.</p>
        </div>}
      </section>}
      <div className="flex items-center gap-0">
        {STEPS.map(({ n, label }, i) => (
          <div key={n} className="flex items-center">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
              ${step === n ? "bg-[#0D1B3E] text-white"
              : step > n  ? "bg-green-100 text-green-700"
              :              "bg-slate-100 text-slate-400"}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold
                ${step === n ? "bg-white text-[#0D1B3E]"
                : step > n  ? "bg-green-500 text-white"
                :              "bg-slate-300 text-white"}`}>
                {n}
              </span>
              <span className="hidden sm:block">{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-5 h-px bg-slate-300 mx-1" />}
          </div>
        ))}
      </div>

      {/* ── Step 1: Module + File ── */}
      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Modül Seçin
            </label>
            <ModuleSelector modules={MODULES} selected={modul} onSelect={setModul} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Dosya Seçin
            </label>
            <FileUploader file={file} onChange={setFile} />
          </div>
          <button
            data-testid={EXCEL.importButton}
            onClick={handleFilePreview}
            disabled={!modul || !file || loading}
            className="bg-[#0D1B3E] text-white rounded-md px-6 py-2.5 text-sm font-medium hover:bg-[#172A58] disabled:opacity-50 transition-colors"
          >
            {loading ? "Önizleniyor..." : "Önizle ve Devam Et"}
          </button>
        </div>
      )}

      {/* ── Step 2: Preview + Field mapping ── */}
      {step === 2 && preview && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 text-sm text-slate-600">
            <span className="font-medium text-slate-800">{preview.total_rows.toLocaleString("tr-TR")}</span> satır,{" "}
            <span className="font-medium text-slate-800">{preview.columns.length}</span> sütun —{" "}
            <span className="font-mono text-xs text-slate-400">{file?.name}</span>
          </div>

          {/* Preview table */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-slate-800">
              Önizleme (İlk 10 Satır)
            </div>
            <div data-testid={EXCEL.previewTable} className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {preview.columns.map((c) => (
                      <th key={c} className="px-3 py-2 text-left font-semibold text-slate-500 whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={`preview-row-${i}`} className="border-b border-slate-100">
                      {preview.columns.map((c) => (
                        <td key={c} className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[120px] truncate">
                          {row[c] != null ? String(row[c]) : "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Field mapping */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-4">Alan Eşleme</h2>
            <FieldMapper
              columns={preview.columns}
              suggestedFields={preview.suggested_fields}
              requiredFields={preview.required_fields}
              mapping={fieldMapping}
              onChange={setFieldMapping}
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Geri
              </button>
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-5 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors"
              >
                {loading
                  ? "Başlatılıyor..."
                  : `${preview.total_rows.toLocaleString("tr-TR")} Kayıt Aktarmaya Başla`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Background job progress ── */}
      {step === 3 && jobStatus && <ImportProgress jobStatus={jobStatus} />}

      {/* ── Step 4: Result ── */}
      {step === 4 && result && <ImportResult result={result} onReset={reset} />}

      {/* ── Upload logs drawer ── */}
      {showLogs && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-200 text-sm font-semibold text-slate-800">
            Yükleme Geçmişi
          </div>
          {logs.length === 0 ? (
            <p className="px-5 py-8 text-center text-slate-400 text-sm">Geçmiş bulunamadı</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Tarih", "Modül", "Dosya", "Eklenen", "Güncellenen", "Hata", "Durum"].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{fmtDateTime(log.tarih)}</td>
                    <td className="px-4 py-3 text-xs font-medium text-slate-700">
                      {MODULES.find((m) => m.value === log.modul)?.label ?? log.modul}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[150px]">{log.dosya_adi}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-green-600">
                      <button disabled={!log.eklenen} onClick={() => openLogDetail(log, "inserted")} className="enabled:underline enabled:hover:text-green-800 disabled:cursor-default">{log.eklenen}</button>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-blue-600">{log.guncellenen}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-red-600">
                      <button disabled={!log.hata_sayisi} onClick={() => openLogDetail(log, "errors")} className="enabled:underline enabled:hover:text-red-800 disabled:cursor-default">{log.hata_sayisi}</button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${
                        log.durum === "tamamlandi"
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {log.durum}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selectedLog && (
            <div className="border-t border-slate-200 bg-red-50/60 p-5">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-red-800">{selectedLog.dosya_adi} — Hatalı Satırlar</h3>
                  <p className="text-xs text-red-600 mt-0.5">{selectedLog.hata_sayisi} hata; ayrıntılar 30 gün saklanır.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={downloadErrors} disabled={!selectedLog.errors?.length} className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50">CSV İndir</button>
                  <button onClick={() => setSelectedLog(null)} className="rounded-md px-2 py-1.5 text-xs text-slate-500 hover:bg-white">Kapat</button>
                </div>
              </div>
              {selectedLog.errors?.length ? (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-red-200 bg-white divide-y divide-red-100">
                  {selectedLog.errors.map((error, index) => <p key={`${error}-${index}`} className="px-3 py-2 text-xs text-red-700 break-words">{error}</p>)}
                </div>
              ) : <p className="text-xs text-slate-500">Bu kaydın ayrıntıları saklama süresi dolduğu için temizlenmiş.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
